"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { MOBILE_TITLE_RETURN_DELAY_MS } from "./constants";

type UseMobileHeaderSearchResult = {
  /** Controls the mobile search UI (search icon / expanding input). */
  isMobileSearchOpen: boolean;
  /** Hides the header title block while search is open (and a bit after close to avoid jumps). */
  isMobileTitleHidden: boolean;
  /** Use this as `onOpenChange` for the mobile search component. */
  setMobileSearchOpen: (open: boolean) => void;
};

export function useMobileHeaderSearch(): UseMobileHeaderSearchResult {
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
  const [isMobileTitleHidden, setIsMobileTitleHidden] = useState(false);
  const titleTimerRef = useRef<number | null>(null);

  const clearTimer = useCallback(() => {
    if (titleTimerRef.current) {
      window.clearTimeout(titleTimerRef.current);
      titleTimerRef.current = null;
    }
  }, []);

  useEffect(() => clearTimer, [clearTimer]);

  const setMobileSearchOpen = useCallback(
    (open: boolean) => {
      setIsMobileSearchOpen(open);

      // Hide the title immediately on open (avoid overflow),
      // but show it only after the close animation finishes (avoid "jump").
      clearTimer();

      if (open) {
        setIsMobileTitleHidden(true);
        return;
      }

      titleTimerRef.current = window.setTimeout(() => {
        setIsMobileTitleHidden(false);
        titleTimerRef.current = null;
      }, MOBILE_TITLE_RETURN_DELAY_MS);
    },
    [clearTimer],
  );

  return { isMobileSearchOpen, isMobileTitleHidden, setMobileSearchOpen };
}

